/**
 * Tabs carousel
 * ----------------------------------------------------------------------------
 * Drives both tabs sections, "Collection tabs: Carousel" and "Product tabs:
 * Carousel". It never looks at what is inside a card, only at the scroller
 * around them, so one copy serves both and whatever card comes next.
 *
 * Two small jobs, kept apart on purpose:
 *
 *   1. Switching tabs, which is a WAI-ARIA tabs widget.
 *   2. Driving each panel's carousel arrows and progress bar.
 *
 * Everything is delegated from the document, so a section re-rendered by the
 * theme editor needs no re-binding and there is nothing to tear down.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO
 *
 * It does not scroll the track itself, does not track an index, and does not
 * know how wide a card is. The track is a native scroll-snap scroller: the
 * browser already handles touch, trackpad, shift-wheel, keyboard and momentum,
 * and it does all of them better than a re-implementation would. The arrows
 * call `scrollBy` and the progress bar reads `scrollLeft`. Both are answers to
 * "where is the browser now", never instructions about where it should be.
 *
 * That is what keeps a hidden panel correct. A component holding its own index
 * has to be told that a panel was hidden and shown again, and told again when
 * the viewport changes the card width. A scroller that is asked has nothing to
 * be told.
 *
 * The one exception is the drag at the bottom of this file, which a native
 * scroller has no answer for on a mouse. It is written so that it hands the
 * scroller straight back when the gesture ends.
 */

(() => {
  const SECTION = '.pebble-tabs';

  /*
   * The settle after a drag, and the arrows, ask for smooth scrolling
   * explicitly, and an explicit `behavior` beats the `scroll-behavior: auto`
   * that the stylesheet sets under reduced motion. So the preference is read
   * here too, live rather than once, because someone can change it mid-session.
   */
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const glide = () => (reducedMotion.matches ? 'auto' : 'smooth');

  // Theme blocks must render their own labels and panels. Move the existing
  // buttons, never clone them: IDs, editor hooks and native product cards stay
  // intact. Without JS the original wrapped layout remains usable.
  const syncTabStrip = (list) => {
    const max = Math.max(0, list.scrollWidth - list.clientWidth);
    const rtl = getComputedStyle(list).direction === 'rtl';
    const progress = Math.max(0, Math.min(max, rtl ? -list.scrollLeft : list.scrollLeft));
    list.toggleAttribute('data-fade-left', rtl ? progress < max - 1 : progress > 1);
    list.toggleAttribute('data-fade-right', rtl ? progress > 1 : progress < max - 1);
    list.toggleAttribute('data-pebble-draggable', max > 1);
  };
  const stripObservers = new Map();
  const initTabStrips = () => {
    for (const [list, observer] of stripObservers) {
      if (!list.isConnected) {
        observer.disconnect();
        stripObservers.delete(list);
      }
    }
    document.querySelectorAll(SECTION).forEach((section) => {
      const layout = section.querySelector('.pebble-product-tabs__layout');
      const list = section.querySelector('.pebble-product-tabs__tablist');
      if (layout && list) {
        const header = document.createElement('div');
        header.className = 'pebble-tabs__header pebble-tabs__scroll-header';
        const heading = layout.querySelector(':scope > .pebble-tabs__heading');
        if (heading) header.append(heading);
        layout.querySelectorAll(':scope > .pebble-product-tabs__tab').forEach((tab) => list.append(tab));
        list.className = 'pebble-tabs__list';
        list.removeAttribute('aria-owns');
        header.append(list);
        layout.prepend(header);
        layout.classList.add('pebble-product-tabs__layout--scroll-tabs');
      }
      section.querySelectorAll('.pebble-tabs__header .pebble-tabs__list').forEach((strip) => {
        strip.parentElement.classList.add('pebble-tabs__scroll-header');
        strip.setAttribute('data-pebble-tab-strip', '');
        if (!stripObservers.has(strip)) {
          const observer = new ResizeObserver(() => syncTabStrip(strip));
          observer.observe(strip);
          [...strip.children].forEach((tab) => observer.observe(tab));
          stripObservers.set(strip, observer);
        }
        syncTabStrip(strip);
      });
    });
  };

  const revealTab = (tab) => {
    const list = tab.closest('[data-pebble-tab-strip]');
    if (!list) return;
    const bounds = list.getBoundingClientRect();
    const item = tab.getBoundingClientRect();
    // Move only this horizontal strip, not the document or product carousel.
    const delta = item.left < bounds.left + 24 ? item.left - bounds.left - 24
      : item.right > bounds.right - 24 ? item.right - bounds.right + 24 : 0;
    if (delta) list.scrollBy({ left: delta, behavior: 'instant' });
    syncTabStrip(list);
  };

  /**
   * How far one card is from the next, measured off two cards that are on
   * screen rather than read from a setting.
   *
   * The pitch already includes the gap and it is already whatever the current
   * breakpoint made it, so there is no second copy of the column count to keep
   * in step with the CSS.
   */
  const cardPitch = (track) => {
    const cards = track.children;
    return cards.length > 1
      ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left
      : track.clientWidth;
  };

  /* ------------------------------------------------------------------ tabs */

  /**
   * Shows one panel and hides its siblings.
   *
   * `hidden` rather than a class, because it takes the panel out of the
   * accessibility tree as well as off the screen. A panel hidden with opacity
   * alone is still read aloud and still focusable, so a screen reader user
   * hears three sets of categories and a keyboard user tabs into cards nobody
   * can see.
   */
  const selectTab = (tab, { focus = false } = {}) => {
    const section = tab.closest(SECTION);
    if (!section) return;

    section.querySelectorAll('[role="tab"]').forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      /* Only the selected tab is in the tab order; arrows move within the strip. */
      other.tabIndex = selected ? 0 : -1;

      const panel = section.querySelector(`#${CSS.escape(other.getAttribute('aria-controls'))}`);
      if (panel) panel.toggleAttribute('hidden', !selected);
    });

    if (focus) tab.focus({ preventScroll: true });
    revealTab(tab);

    /*
     * The newly shown panel has never been measured: it had no width while it
     * was hidden, so its arrows and progress bar are still at their defaults.
     */
    const panel = section.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
    if (panel) sync(panel.querySelector('[data-pebble-track]'));
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.('[role="tab"]');
    if (tab) selectTab(tab);
  });

  /**
   * Arrow keys move between tabs, Home and End jump to the ends.
   *
   * Moving focus also selects, which is the right choice for tabs whose panels
   * are already in the page: there is nothing to load, so making someone press
   * an extra key to see what they have arrowed onto is a step for its own sake.
   */
  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[role="tab"]');
    if (!tab || !tab.closest(SECTION)) return;

    const tabs = [...tab.closest(SECTION).querySelectorAll('[role="tab"]')];
    const current = tabs.indexOf(tab);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectTab(tabs[next], { focus: true });
  });

  /* -------------------------------------------------------------- carousel */

  /**
   * Reads the scroller and writes what the controls should show.
   *
   * Called on scroll, on resize, and whenever a panel is shown, because those
   * are the three ways the answer can change. Nothing here is stored between
   * calls: the scroller is the state.
   */
  const sync = (track) => {
    if (!track) return;

    const overflow = track.scrollWidth - track.clientWidth;

    /*
     * Whether the row can be dragged is the same question as whether the arrows
     * are worth showing, so it is answered once, here, and the stylesheet reads
     * the answer to decide whether to offer a grab cursor.
     */
    track.toggleAttribute('data-pebble-draggable', overflow > 1);

    const panel = track.closest('.pebble-tabs__panel');
    const controls = panel?.querySelector('[data-pebble-controls]');
    if (!controls) return;

    /*
     * Nothing to scroll means nothing to control. Hiding the whole row is
     * better than showing two dead arrows and a bar that is always full: a
     * control that cannot do anything still invites the click that proves it.
     */
    controls.hidden = overflow <= 1;
    if (controls.hidden) return;

    const progress = Math.min(1, Math.max(0, track.scrollLeft / overflow));
    controls.style.setProperty('--pebble-tabs-progress', String(progress));

    /*
     * A pixel of slack at each end. Sub-pixel layout means `scrollLeft` rarely
     * lands exactly on zero or exactly on the maximum, and without the slack
     * the arrow at the end a shopper has just reached stays stubbornly enabled.
     */
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= overflow - 1;

    controls.querySelectorAll('[data-pebble-scroll]').forEach((button) => {
      const forward = Number(button.dataset.pebbleScroll) > 0;
      button.disabled = forward ? atEnd : atStart;
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-pebble-scroll]');
    if (!button) return;

    const track = button.closest('.pebble-tabs__panel')?.querySelector('[data-pebble-track]');
    if (!track) return;

    /* One card at a time, and the same measurement the drag settles against. */
    track.scrollBy({
      left: cardPitch(track) * Number(button.dataset.pebbleScroll),
      behavior: glide(),
    });
  });

  /*
   * `passive` because this listener never calls `preventDefault`, and saying so
   * lets the browser scroll without waiting to find out.
   */
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (track instanceof HTMLElement && track.matches('[data-pebble-track]')) sync(track);
      if (track instanceof HTMLElement && track.matches('[data-pebble-tab-strip]')) syncTabStrip(track);
    },
    { passive: true, capture: true }
  );

  const syncAll = () => {
    initTabStrips();
    document.querySelectorAll('[data-pebble-track]').forEach(sync);
  };

  /*
   * A resize changes the card width, which changes both how far an arrow should
   * travel and whether there is anything left to travel to.
   */
  window.addEventListener('resize', syncAll);
  document.addEventListener('shopify:section:load', syncAll);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(initTabStrips));
  document.addEventListener('shopify:block:select', (event) => {
    const tab = event.target.closest?.('.pebble-tabs__tab');
    if (tab) selectTab(tab);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }

  /*
   * Images arrive after the first measurement and can change `scrollWidth`, so
   * the controls are re-checked once the section has finished loading.
   */
  window.addEventListener('load', syncAll);

  /* --------------------------------------------------------- drag to scroll */

  /**
   * Grab the row and pull it.
   *
   * MOUSE ONLY, ON PURPOSE
   *
   * A finger and a trackpad already move this row, and they move it better than
   * anything written here could: the browser gives them momentum, rubber-band
   * ends and a scroll that never leaves the compositor. Taking those gestures
   * over to re-implement them worse is the usual way carousels end up feeling
   * heavier on a phone than the page they sit in. So the first thing this does
   * is let every pointer that is not a mouse fall through untouched.
   *
   * WHAT IT COPIES FROM PEBBLE
   *
   * Pebble drives its carousels with Swiper. Read off the live theme:
   *
   *    threshold 5        followFinger true     touchRatio 1
   *    longSwipes true    longSwipesRatio 0.5   longSwipesMs 300
   *    shortSwipes true   slidesPerGroup 1      speed 300
   *    preventClicks true
   *
   * which describes a gesture in three parts, and all three are below: nothing
   * happens under 5px so a click is still a click; the row follows the cursor
   * exactly while the button is down; and on release it goes to ONE card, never
   * more, decided by either distance (past half a card) or speed (let go inside
   * 300ms). The second is what makes a flick work — a short fast pull is a
   * shopper saying "next", and asking them to also drag half a card for it is
   * how a carousel comes to feel stuck.
   *
   * WHY THERE IS NO `setPointerCapture` HERE
   *
   * Capturing the pointer is the textbook way to keep a drag alive once the
   * cursor leaves the element, and it was the first thing this did. It also
   * freezes hover: while an element holds the capture the browser stops
   * updating `:hover` for whatever is under the cursor, and on release it does
   * not recompute until the pointer moves again. So a shopper who dragged a row
   * and then let go without moving was left with the card they had grabbed
   * still hovered — its quick-add sitting there over a photograph, on a card
   * that was no longer under the cursor, until they happened to move the mouse.
   *
   * Listening on the document does the same job without that: events keep
   * arriving wherever the cursor goes in the window, and the `buttons === 0`
   * guard below covers the one case capture was really protecting against, a
   * button released somewhere this listener never heard about.
   */

  const DRAG_THRESHOLD = 5; /* Swiper `threshold` */
  const SHORT_SWIPE_MS = 300; /* Swiper `longSwipesMs` */
  const LONG_SWIPE_RATIO = 0.5; /* Swiper `longSwipesRatio` */

  /** The gesture in flight, or null. One at a time, by construction. */
  let drag = null;
  // A late scrollend from an earlier gesture must never re-enable snap while
  // the next gesture is moving. Own and cancel both completion paths per track.
  const pendingSettles = new WeakMap();

  document.addEventListener('pointerdown', (event) => {
    /* Left button of an actual mouse, and nothing else. */
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = event.target.closest?.('[data-pebble-track], [data-pebble-tab-strip]');
    if (!track || track.scrollWidth - track.clientWidth <= 1) return;

    pendingSettles.get(track)?.();
    track.scrollTo({ left: track.scrollLeft, behavior: 'instant' });

    /*
     * Armed, not started. Until the pointer has travelled far enough this is
     * indistinguishable from a click on a card, and it has to stay that way:
     * blocking selection here would break every click in the row on the chance
     * that one of them was a drag.
     */
    drag = {
      track,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScroll: track.scrollLeft,
      startTime: event.timeStamp,
      active: false,
    };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    /*
     * The button came up somewhere this listener never heard about — released
     * outside the window, or swallowed by something else. Finish on the next
     * movement rather than staying latched to a button that is no longer down.
     * This is the guard that lets the drag work without capturing the pointer.
     */
    if (event.buttons === 0) {
      endDrag(event);
      return;
    }

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (!drag.active) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;

      /*
       * More vertical than horizontal means the page is what they meant to
       * move, not the row. Letting go of the gesture entirely is the right
       * answer: a scroller that grabs a downward drag off a tall product image
       * traps the page.
       */
      if (Math.abs(dy) > Math.abs(dx)) {
        drag = null;
        return;
      }

      drag.active = true;
      drag.track.toggleAttribute('data-pebble-dragging', true);
      drag.track.toggleAttribute('data-pebble-settling', true);
    }

    /* Stops the text selection a horizontal drag across labels would start. */
    event.preventDefault();

    /*
     * Written straight, 1:1 with the cursor, from the position the gesture
     * started at rather than from the last frame. Accumulating deltas drifts;
     * this cannot, because it is recomputed from the origin every move.
     */
    drag.track.scrollLeft = drag.startScroll - dx;
  });

  const endDrag = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    const gesture = drag;
    drag = null;

    /* Never started: this was a click, and a click is none of our business. */
    if (!gesture.active) return;

    const { track } = gesture;

    /*
     * The cursor and the selection block end with the button, not with the
     * glide. Snap stays off, under the other attribute, until the row arrives.
     */
    track.toggleAttribute('data-pebble-dragging', false);

    /*
     * Swiper's `preventClicks`. A drag that ends on a card is still, as far as
     * the browser is concerned, a click on that card, and on a product card the
     * whole tile is a link. So the click that is about to be dispatched is
     * swallowed once, in the capture phase, before it can reach anything. The
     * timeout is for the release that produces no click at all.
     */
    const swallowClick = (click) => {
      click.stopPropagation();
      click.preventDefault();
    };
    document.addEventListener('click', swallowClick, { capture: true, once: true });
    setTimeout(() => document.removeEventListener('click', swallowClick, { capture: true }), 0);

    // Tabs have different widths and no card snap. Preserve the exact dragged
    // position rather than applying the product carousel's one-card settle.
    if (track.matches('[data-pebble-tab-strip]')) {
      track.removeAttribute('data-pebble-settling');
      syncTabStrip(track);
      return;
    }

    const step = cardPitch(track);
    const cards = [...track.children];
    const elapsed = event.timeStamp - gesture.startTime;
    const forward = event.clientX < gesture.startX;

    /*
     * Which card it lands on is decided from where the row was DRAGGED TO, not
     * from where the gesture began. That distinction is the whole difference
     * between a carousel that follows the hand and one that argues with it: a
     * two-card pull should end two cards along, and only the last part of a
     * card is in question.
     *
     * `landed` is the card the row is sitting on top of and `past` is how far
     * into the next one it has come, 0 to 1. Then Swiper's two rules:
     *
     *   a slow drag  keeps the card it is more than half onto
     *                (`longSwipesRatio: 0.5`)
     *   a fast flick takes the next one in the direction of travel whatever
     *                the distance (`shortSwipes`, under `longSwipesMs`)
     *
     * The second is what makes a small quick pull work. Someone who flicks the
     * row 20px is saying "next", and answering that by sliding back to where
     * they started is how a carousel comes to feel stuck.
     */
    const landed = Math.floor(track.scrollLeft / step);
    const past = track.scrollLeft / step - landed;
    const advance = elapsed < SHORT_SWIPE_MS ? forward : past >= LONG_SWIPE_RATIO;
    const to = Math.min(cards.length - 1, Math.max(0, landed + (advance ? 1 : 0)));

    const target = cards[to];
    if (target) {
      const left =
        track.scrollLeft + target.getBoundingClientRect().left - track.getBoundingClientRect().left;
      track.scrollTo({ left, behavior: glide() });
    }

    /*
     * Snap comes back once the row has stopped, and by then it is already
     * parked on a card, so switching it on moves nothing. Doing it any earlier
     * is visible: the browser re-snaps the instant the property changes, so the
     * row would jump to the nearest card and only then glide to the right one.
     *
     * `scrollend` is the honest signal and the timeout is the backstop, because
     * a settle that has nowhere to travel never fires one.
     */
    let restoreTimer;
    const restore = () => {
      clearTimeout(restoreTimer);
      track.removeEventListener('scrollend', restore);
      track.toggleAttribute('data-pebble-settling', false);
      pendingSettles.delete(track);
    };
    pendingSettles.set(track, restore);
    track.addEventListener('scrollend', restore, { once: true });
    restoreTimer = setTimeout(restore, 400);
  };

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
  window.addEventListener('blur', () => {
    if (!drag?.track.matches('[data-pebble-tab-strip]')) return;
    drag.track.removeAttribute('data-pebble-dragging');
    drag.track.removeAttribute('data-pebble-settling');
    drag = null;
  });

  /*
   * The browser's own image drag would otherwise start on the first pixel of
   * movement and replace the gesture with a ghost of a product photo.
   */
  document.addEventListener('dragstart', (event) => {
    if (event.target.closest?.('[data-pebble-track]')) event.preventDefault();
  });
})();
